import { useEffect, useRef, useState } from "react";
import { useMedia } from "./use-media";

export function useInViewport<T extends HTMLElement>(
  options: IntersectionObserverInit = { threshold: 0.4 },
) {
  const { isSmallerScreen } = useMedia();
  const [isInView, setIsInView] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const elementRef = useRef<T>(null);

  // Update options based on screen size
  if (isSmallerScreen) {
    options = { threshold: 0.2 };
  }

  useEffect(() => {
    // Mark that we're now on the client side
    setIsClient(true);
  }, []);

  useEffect(() => {
    // Only initialize Intersection Observer on the client side
    if (!isClient || typeof window === "undefined") return;
    
    const element = elementRef.current;
    if (!element) return;

    // Check if Intersection Observer is supported
    if (typeof IntersectionObserver === "undefined") {
      // Fallback: assume element is in view
      setIsInView(true);
      setHasAnimated(true);
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        if (!hasAnimated) {
          setHasAnimated(true);
        }
      } else {
        if (!hasAnimated) {
          setHasAnimated(true);
          setIsInView(false);
        }
      }
    }, options);

    observer.observe(element);

    return () => observer.disconnect();
  }, [isClient, options, hasAnimated]);

  return { ref: elementRef, isInView, hasAnimated };
}
